import { Node } from '@xyflow/react';
import { useMemo, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { APP_VERSION } from 'src/config';
import { NodeModel } from 'src/modules/network-configuration/models/node-model';
import { ProjectJsonData } from 'src/types/project';

import { useUndoRedo } from '../undo-redo';
import { buildProjectJsonData } from './build-project-json-data/build-project-json-data';
import { IMPORT_CONFIGURATION_STEPS } from './import-configuration-steps';
import {
  getEdgeIds,
  canLeaveStep,
  getSwitchNodeIds,
  getDeviceLikeNodeIds,
} from './import-review-helpers';
import { loadImportedConfigFiles } from './load-imported-config';
import {
  IConfigImportSummary,
  IImportConfigurationState,
  ImportConfigurationContextType,
} from './types';
import { useImportReviewState } from './use-import-review-state';

const dedupeWarnings = (warnings: string[]) => [...new Set(warnings)];

export function useImportConfigurationController(
  onApplyImport: (data: ProjectJsonData) => void,
): ImportConfigurationContextType {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const { clearAll } = useUndoRedo();
  const [dialogState, setDialogState] =
    useState<IImportConfigurationState | null>(null);
  const [summary, setSummary] = useState<IConfigImportSummary | null>(null);
  const {
    importReview,
    resetImportReview,
    setVlanSubnetConfirmed,
    setGlobalSettingsConfirmed,
    setNodeReviewed,
    setEdgeReviewed,
    clearNodeReview,
    clearEdgeReview,
  } = useImportReviewState();

  const onImportConfiguration = useCallback(() => {
    const isCleared = clearAll();
    if (!isCleared) return;
    setSummary(null);
    setDialogState(null);
    setCurrentStep(1);
    resetImportReview();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.cfg,.zip';
    input.multiple = true;
    input.onchange = async (event) => {
      const files = Array.from((event.target as HTMLInputElement).files || []);
      if (!files.length) return;
      try {
        const loaded = await loadImportedConfigFiles(files);
        if (!loaded.decodedFiles.length) {
          alert('No importable configuration files were found');
          return;
        }
        setDialogState({
          decodedFiles: loaded.decodedFiles,
          projectJsonData: null,
          failedFiles: loaded.failedFiles,
          warnings: dedupeWarnings([...loaded.warnings]),
        });
      } catch (error) {
        alert(
          error instanceof Error
            ? error.message
            : 'Failed to import configuration',
        );
      }
    };
    input.click();
  }, [clearAll, resetImportReview]);

  const confirmImport = useCallback(() => {
    if (!dialogState) return;
    if (!dialogState.projectJsonData) {
      toast.error('Failed to import configuration');
      return;
    }
    onApplyImport({
      ...dialogState.projectJsonData,
      version: APP_VERSION,
    });
    setSummary(null);
    setDialogState(null);
    resetImportReview();
  }, [dialogState, onApplyImport, resetImportReview]);

  const updateProjectJsonData = useCallback(
    (updater: (previous: ProjectJsonData) => ProjectJsonData) => {
      setDialogState((previousState) => {
        if (!previousState?.projectJsonData) return previousState;
        return {
          ...previousState,
          projectJsonData: updater(previousState.projectJsonData),
        };
      });
    },
    [],
  );

  const removeImportedFile = useCallback((fileId: string) => {
    setDialogState((previousState) => {
      if (!previousState) return previousState;
      const decodedFiles = previousState.decodedFiles.filter(
        (decodedFile) => decodedFile.id !== fileId,
      );
      return { ...previousState, decodedFiles };
    });
  }, []);

  const handlePrev = useCallback(() => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (!dialogState) return;
    if (currentStep === 1) {
      if (dialogState.decodedFiles.length === 0) {
        toast.error('Add at least one configuration file.');
        return;
      }
      const projectJsonData = buildProjectJsonData(dialogState.decodedFiles);
      setDialogState((previousState) => {
        if (!previousState) return previousState;
        return { ...previousState, projectJsonData };
      });
      resetImportReview();
    } else if (dialogState.projectJsonData) {
      const pj = dialogState.projectJsonData;
      const switchIds = getSwitchNodeIds(pj.nodes as Node<NodeModel>[]);
      const deviceIds = getDeviceLikeNodeIds(pj.nodes as Node<NodeModel>[]);
      const edgeIds = getEdgeIds(pj.edges);
      if (
        !canLeaveStep(currentStep, importReview, switchIds, deviceIds, edgeIds)
      ) {
        if (currentStep === 2) {
          toast.error('Confirm VLAN & subnet review before continuing.');
        } else if (currentStep === 3) {
          toast.error('Confirm every switch before continuing.');
        } else if (currentStep === 4) {
          toast.error('Confirm every device before continuing.');
        } else if (currentStep === 5) {
          toast.error('Confirm every connection before continuing.');
        } else if (currentStep === 6) {
          toast.error('Confirm global settings before continuing.');
        }
        return;
      }
    }
    if (currentStep < IMPORT_CONFIGURATION_STEPS.length)
      setCurrentStep(currentStep + 1);
  }, [currentStep, dialogState, importReview, resetImportReview]);

  const canProceedFromCurrentStep = useMemo(() => {
    if (!dialogState) return false;
    if (currentStep === 1) return dialogState.decodedFiles.length > 0;
    if (currentStep === 7) return true;
    if (!dialogState.projectJsonData) return false;
    const pj = dialogState.projectJsonData;
    const switchIds = getSwitchNodeIds(pj.nodes as Node<NodeModel>[]);
    const deviceIds = getDeviceLikeNodeIds(pj.nodes as Node<NodeModel>[]);
    const edgeIds = getEdgeIds(pj.edges);
    return canLeaveStep(
      currentStep,
      importReview,
      switchIds,
      deviceIds,
      edgeIds,
    );
  }, [currentStep, dialogState, importReview]);

  return useMemo(
    () => ({
      currentStep,
      handlePrev,
      handleNext,
      onImportConfiguration,
      confirmImport,
      removeImportedFile,
      dialogState,
      summary,
      updateProjectJsonData,
      importReview,
      setVlanSubnetConfirmed,
      setGlobalSettingsConfirmed,
      setNodeReviewed,
      setEdgeReviewed,
      clearNodeReview,
      clearEdgeReview,
      canProceedFromCurrentStep,
      closeDialog: () => {
        setDialogState(null);
        setSummary(null);
        resetImportReview();
      },
    }),
    [
      currentStep,
      handlePrev,
      handleNext,
      onImportConfiguration,
      confirmImport,
      removeImportedFile,
      dialogState,
      summary,
      updateProjectJsonData,
      importReview,
      setVlanSubnetConfirmed,
      setGlobalSettingsConfirmed,
      setNodeReviewed,
      setEdgeReviewed,
      clearNodeReview,
      clearEdgeReview,
      canProceedFromCurrentStep,
      resetImportReview,
    ],
  );
}
